import type { FederationRuntimePlugin } from '@module-federation/enhanced/runtime'

export const REMOTE_ENTRY_CACHE_BUST_PARAM = '?v='
export const MF_INVALIDATE_REMOTE_ENTRY_EVENT = 'alef:mf-invalidate-remote-entry'

type RemoteWithEntry = { entry: string }

let remoteEntryBustGeneration = 0
let appliedRemoteEntryBustGeneration = -1
let navigationBustListenerInstalled = false

export function withRemoteEntryCacheBust(entry: string, timestamp: number = Date.now()): string {
  const [base] = entry.split(REMOTE_ENTRY_CACHE_BUST_PARAM)
  return `${base}${REMOTE_ENTRY_CACHE_BUST_PARAM}${timestamp}.js`
}

/**
 * Ask MF hosts/remotes to re-stamp remoteEntry ?v= on the next beforeRequest.
 * Uses a window event so UI packages do not import the rspack runtime plugin.
 */
export function requestRemoteEntryCacheBust(): void {
  if (typeof window === 'undefined') {
    remoteEntryBustGeneration += 1
    return
  }
  window.dispatchEvent(new CustomEvent(MF_INVALIDATE_REMOTE_ENTRY_EVENT))
}

function ensureRemoteEntryHasCacheBust(remote: RemoteWithEntry): void {
  if (!remote.entry.includes(REMOTE_ENTRY_CACHE_BUST_PARAM)) {
    remote.entry = withRemoteEntryCacheBust(remote.entry)
  }
}

function applyRemoteEntryCacheBustPolicy(remotes: Array<RemoteWithEntry | object>): void {
  if (remoteEntryBustGeneration === appliedRemoteEntryBustGeneration) {
    remotes.forEach((remote) => {
      if ('entry' in remote) {
        ensureRemoteEntryHasCacheBust(remote as RemoteWithEntry)
      }
    })
    return
  }

  remotes.forEach((remote) => {
    if ('entry' in remote) {
      ;(remote as RemoteWithEntry).entry = withRemoteEntryCacheBust((remote as RemoteWithEntry).entry)
    }
  })
  appliedRemoteEntryBustGeneration = remoteEntryBustGeneration
}

function installNavigationRemoteEntryBustListener(): void {
  if (typeof window === 'undefined' || navigationBustListenerInstalled) {
    return
  }
  navigationBustListenerInstalled = true

  const bust = () => {
    remoteEntryBustGeneration += 1
  }

  window.addEventListener('popstate', bust)
  // Teacher host uses HashRouter — hash changes must refresh remoteEntry too.
  window.addEventListener('hashchange', bust)
  window.addEventListener(MF_INVALIDATE_REMOTE_ENTRY_EVENT, bust)

  // Patch pushState only. replaceState is used for query/state updates and would
  // over-bust remoteEntry across all MF hosts/remotes that load this plugin.
  const originalPushState = window.history.pushState.bind(window.history)

  window.history.pushState = ((...args: Parameters<History['pushState']>) => {
    const result = originalPushState(...args)
    bust()
    return result
  }) as History['pushState']
}

const runtimePlugin: () => FederationRuntimePlugin = function () {
  return {
    name: 'invalidate-remote-entry-cache',
    beforeInit(args) {
      installNavigationRemoteEntryBustListener()
      // Session start: stamp once so remoteEntry is not an unversioned long-lived URL.
      remoteEntryBustGeneration += 1
      applyRemoteEntryCacheBustPolicy(args.options.remotes)
      return args
    },
    beforeRequest(args) {
      // Re-stamp only after navigation / explicit invalidate — not on every remote request.
      applyRemoteEntryCacheBustPolicy(args.origin.options.remotes)
      return args
    },
  }
}

export default runtimePlugin
